import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Scale, AlertCircle, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';

export default function TermsOfService() {
  const lastUpdated = "November 16, 2025";

  return (
    <article className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-blue-100 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center mb-6">
            <Scale className="h-12 w-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-xl text-blue-100">
            Last updated: {lastUpdated}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-8 rounded-r-lg">
          <p className="text-gray-700 leading-relaxed">
            Please read these Terms of Service carefully before using CyberEdge's website and services. By accessing or using our services, you agree to be bound by these terms.
          </p>
        </div>

        <section className="mb-12">
          <div className="flex items-center mb-4">
            <FileText className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Agreement to Terms</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              By accessing and using CyberEdge's website and services ("Services"), you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these Terms of Service, you should not access or use our Services.
            </p>
            <p className="mb-4">
              These Terms apply to all visitors, users, and others who access or use our Services. We reserve the right to update or modify these Terms at any time without prior notice. Your continued use of the Services following any changes constitutes acceptance of those changes.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <div className="flex items-center mb-4">
            <CheckCircle className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Use of Services</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700">
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Permitted Use</h3>
            <p className="mb-4">You may use our Services only for lawful purposes and in accordance with these Terms. You agree to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Use the Services in compliance with all applicable laws</li>
              <li>Respect intellectual property rights</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">Prohibited Activities</h3>
            <p className="mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Use the Services for any illegal or unauthorized purpose</li>
              <li>Violate any laws in your jurisdiction</li>
              <li>Infringe upon the rights of others</li>
              <li>Transmit viruses, malware, or other malicious code</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Services or servers</li>
              <li>Use automated systems to access the Services without permission</li>
              <li>Impersonate another person or entity</li>
              <li>Harass, abuse, or harm another person</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Accounts and Registration</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              When you create an account with us, you must provide information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account.
            </p>
            <p className="mb-4">
              You are responsible for safeguarding the password that you use to access the Services and for any activities or actions under your password. You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Intellectual Property Rights</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              The Services and their original content, features, and functionality are and will remain the exclusive property of CyberEdge and its licensors. The Services are protected by copyright, trademark, and other laws of both the United States and foreign countries.
            </p>
            <p className="mb-4">
              Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent. You may not use our intellectual property without our express written permission.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">User Content</h3>
            <p className="mb-4">
              By submitting content to our Services, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display that content in connection with the Services. You represent and warrant that you have all necessary rights to grant this license.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment Terms</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              If you purchase services from us, you agree to provide current, complete, and accurate purchase and account information. You agree to promptly update your account and payment information as necessary.
            </p>
            <p className="mb-4">
              We reserve the right to refuse or cancel orders at any time for reasons including but not limited to: service availability, errors in pricing or product information, or suspected fraudulent or unauthorized transactions.
            </p>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Fees and Billing</h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>All fees are in USD unless otherwise stated</li>
              <li>Subscription fees are billed in advance on a recurring basis</li>
              <li>You authorize us to charge your payment method for all fees</li>
              <li>We may change our fees with 30 days' notice</li>
              <li>Refunds are subject to our refund policy</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <div className="flex items-center mb-4">
            <AlertCircle className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Disclaimer of Warranties</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMISSIBLE PURSUANT TO APPLICABLE LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Implied warranties of merchantability</li>
              <li>Fitness for a particular purpose</li>
              <li>Non-infringement</li>
              <li>Accuracy, reliability, or availability of the Services</li>
              <li>That the Services will be uninterrupted or error-free</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <div className="flex items-center mb-4">
            <XCircle className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Limitation of Liability</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL CYBEREDGE, ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Loss of profits, data, use, goodwill, or other intangible losses</li>
              <li>Damages resulting from your access to or use of or inability to access or use the Services</li>
              <li>Damages resulting from any conduct or content of any third party on the Services</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content</li>
            </ul>
            <p className="mb-4">
              OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATED TO THE SERVICES SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRIOR TO THE CLAIM.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Indemnification</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              You agree to defend, indemnify, and hold harmless CyberEdge and its licensees and licensors, and their employees, contractors, agents, officers, and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs, or debt, and expenses arising from:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Your use of and access to the Services</li>
              <li>Your violation of any term of these Terms</li>
              <li>Your violation of any third-party right, including intellectual property rights</li>
              <li>Any harm caused to any third party through your use of the Services</li>
            </ul>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Termination</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              We may terminate or suspend your account and access to the Services immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.
            </p>
            <p className="mb-4">
              Upon termination, your right to use the Services will immediately cease. If you wish to terminate your account, you may simply discontinue using the Services or contact us to request account deletion.
            </p>
            <p className="mb-4">
              All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Governing Law and Dispute Resolution</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the State of California, United States, without regard to its conflict of law provisions.
            </p>
            <p className="mb-4">
              Any disputes arising out of or relating to these Terms or the Services shall be resolved through binding arbitration in accordance with the American Arbitration Association's rules, except where prohibited by law. The arbitration shall take place in San Francisco, California.
            </p>
            <p className="mb-4">
              You agree to waive any right to a jury trial or to participate in a class action lawsuit or class-wide arbitration.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Severability</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law, and the remaining provisions will continue in full force and effect.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <p className="mb-2"><strong>CyberEdge Solutions</strong></p>
              <p className="mb-2">Email: info@cyberedgesolutions.com</p>
              <p className="mb-2">Phone: +91 6301644590</p>
              <p>Address: PJR Arcade, Kukatpally, Hyderabad, Telangana 500085</p>
            </div>
          </div>
        </section>

        <div className="flex flex-wrap gap-4 pt-8 border-t border-gray-200">
          <Link
            to="/privacy"
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Privacy Policy
          </Link>
          <Link
            to="/cookies"
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Cookie Policy
          </Link>
          <Link
            to="/gdpr"
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            GDPR Compliance
          </Link>
        </div>
      </div>
    </article>
  );
}
